import { useEffect, useMemo, useState } from 'react';

import { validateField } from '@app/dashboard/views/pages/inner_layout/Bills/utils';

import type { IServiceField, TDynamicFieldValue } from '@app/dashboard/types/innerLayout/bills/category-services';

export const useDynamicForm = (fields: IServiceField[]) => {
  const [values, setValues] = useState<Record<string, TDynamicFieldValue>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const resetForm = () => {
    const initial: Record<string, TDynamicFieldValue> = {};

    fields.forEach((field) => {
      initial[field.field_name] = field.default_value ?? '';
    });

    setValues(initial);
    setErrors({});
  };

  const fieldsSignature = useMemo(
    () =>
      fields
        .map((field) => `${field.field_name}:${String(field.default_value ?? '')}`)
        .join('|'),
    [fields],
  );

  useEffect(() => {
    resetForm();
  }, [fieldsSignature]);

  const handleChange = (fieldName: string, value: TDynamicFieldValue, field: IServiceField) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
    setErrors((prev) => ({
      ...prev,
      [fieldName]: validateField(field, String(value)),
    }));
  };

  const isValid = fields
    .filter((f) => !f.is_hidden)
    .every((field) => {
      const value = values[field.field_name];
      const hasValue = value !== '' && value !== undefined && value !== null;
      const hasNoError = !errors[field.field_name];
      return hasValue && hasNoError;
    });

  const getPayload = () => {
    const payload: Record<string, TDynamicFieldValue> = {};

    fields.forEach((field) => {
      payload[field.field_name] = values[field.field_name];
    });

    return payload;
  };

  return { values, errors, handleChange, isValid, getPayload, resetForm };
};
